import React from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import "../css/about.scss";

const toTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const PILLARS = [
  {
    id: "respun",
    label: "The Re-Spun Program",
    copy: "See how we're saving 650,000 lbs of clothes from landfills.",
    iconBg: "#d9e8e8",
    iconSrc: "/main/r1-1.svg",
    linkTo: "/about",
    linkLabel: "Learn more",
    reverse: false,
  },
  {
    id: "story",
    label: "Our Story",
    copy: "We sure have come a long way from selling tees out of a bus.",
    iconBg: "#f3ede4",
    iconSrc: "/main/r1-2.svg",
    linkTo: "/about",
    linkLabel: "Read on",
    reverse: true,
  },
  {
    id: "bcert",
    label: "Proudly a B-Corp",
    copy: "Making clothes that are good for people and the planet.",
    iconBg: "#e4ede0",
    iconSrc: "/main/r1-3.svg",
    linkTo: "/shop",
    linkLabel: "See why",
    reverse: false,
  },
  {
    id: "impact",
    label: "Impact Report",
    copy: "All the nerdy details that show we're not an evil company.",
    iconBg: "#f0e8e4",
    iconSrc: "/main/r1-1.svg",
    linkTo: "/shop",
    linkLabel: "Dig in",
    reverse: true,
  },
];

const STATS = [
  {
    value: "650K+",
    unit: "lbs",
    label: "Textile waste diverted from landfill",
  },
  { value: "B", label: "Certified B Corp since 2020" },
  { value: "50%+", label: "Products made from sustainable materials" },
  { value: "100%", label: "Carbon-neutral shipping commitment" },
];

const VALUES = [
  {
    title: "Made to Last",
    body: "We design clothes meant to be worn hundreds of times, not thrown away after a season. Quality over quantity, always.",
  },
  {
    title: "People First",
    body: "Every person in our supply chain deserves fair wages and safe conditions. We audit partners regularly and publish the results.",
  },
  {
    title: "Keep It Local",
    body: "We keep production close to home whenever we can — cutting carbon and supporting American manufacturers and workers.",
  },
  {
    title: "Radical Transparency",
    body: "From fabric sourcing to carbon offsets, we show our work. If we're not perfect, we'll tell you and keep trying.",
  },
];

function About() {
  return (
    <div className="about">
      <Header />

      <main className="about__main" id="MainContent">
        <section className="about__hero" aria-label="About Mindredlane">
          <div className="about__hero-bg">
            <img
              src="/main/main4-1.jpg"
              alt=""
              aria-hidden="true"
              className="about__hero-img"
            />
            <div className="about__hero-overlay" />
          </div>
          <div className="about__hero-content">
            <h1 className="about__hero-title">About</h1>
            <p className="about__hero-sub">
              We started with a bus and a dream. Now we make clothes that
              matter.
            </p>
          </div>
        </section>

        <section className="about__pillars" aria-label="What we stand for">
          <div className="about__pillars-inner">
            {PILLARS.map((p) => (
              <div
                key={p.id}
                className={`about__pillar ${
                  p.reverse ? "about__pillar--reverse" : ""
                }`}
              >
                <div
                  className="about__pillar-icon-wrap"
                  style={{ background: p.iconBg }}
                >
                  <img
                    src={p.iconSrc}
                    alt=""
                    aria-hidden="true"
                    className="about__pillar-icon"
                  />
                </div>
                <div className="about__pillar-body">
                  <h2 className="about__pillar-title">{p.label}</h2>
                  <p className="about__pillar-copy">{p.copy}</p>
                  <Link
                    className="about__pillar-link"
                    to={p.linkTo}
                    onClick={toTop}
                  >
                    {p.linkLabel}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="about__stats" aria-labelledby="stats-heading">
          <h2 id="stats-heading" className="about__stats-heading">
            By the numbers
          </h2>
          <div className="about__stats-grid">
            {STATS.map((s) => (
              <div key={s.label} className="about__stat">
                <span className="about__stat-value">{s.value}</span>
                {s.unit ? (
                  <span className="about__stat-unit">{s.unit}</span>
                ) : null}
                <p className="about__stat-label">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="about__values" aria-labelledby="values-heading">
          <div className="about__values-inner">
            <h2 id="values-heading" className="about__values-title">
              What drives us
            </h2>
            <div className="about__values-grid">
              {VALUES.map((v) => (
                <div key={v.title} className="about__value-card">
                  <h3 className="about__value-name">{v.title}</h3>
                  <p className="about__value-body">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="about__bcorp" aria-label="B Corp certification">
          <div className="about__bcorp-inner">
            <div className="about__bcorp-badge" aria-hidden="true">
              <img src="/main/r1-3.svg" alt="" className="about__bcorp-icon" />
            </div>
            <h2 className="about__bcorp-title">Certified B Corporation</h2>
            <p className="about__bcorp-body">
              B Corps are businesses that meet the highest standards of verified
              social and environmental performance, public transparency, and
              legal accountability to balance profit and purpose.
            </p>
            <p className="about__bcorp-body">
              Mindredlane is proud to be part of a global movement of people
              using business as a force for good — and we're just getting
              started.
            </p>
          </div>
        </section>

        <section className="about__cta" aria-label="Contact">
          <div className="about__cta-inner">
            <h2 className="about__cta-title">
              Let's keep the conversation going.
            </h2>
            <p className="about__cta-sub">
              Questions, ideas, or just want to say hey? We're all ears.
            </p>
            <div className="about__cta-actions">
              <Link
                className="about__cta-btn about__cta-btn--primary"
                to="/contact"
                onClick={toTop}
              >
                Get in touch
              </Link>
              <Link
                className="about__cta-btn about__cta-btn--outline"
                to="/shop"
                onClick={toTop}
              >
                Shop our collection
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default About;
